import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import contactService from '../utils/contactService';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import Icon from './AppIcon';

const ContactForm = ({
  onSuccess,
  onCancel,
  initialIssueType = null,
  sourcePage = null,
  sourceUrl = null,
  metadata = {},
  showTitle = true,
  compact = false
}) => {
  const [formData, setFormData] = useState({
    issueType: initialIssueType || '',
    subject: '',
    message: '',
    emailNotifications: true // Default to true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const { user } = useAuth();
  const location = useLocation();

  // Auto-detect source page and URL if not provided
  const detectedSourcePage = sourcePage || getPageName(location.pathname);
  const detectedSourceUrl = sourceUrl || location.pathname;

  // Get contextual issue types
  const issueTypeOptions = contactService.getContextualIssueTypes(detectedSourcePage);

  useEffect(() => {
    // Auto-select first suggested issue type if none provided
    if (!initialIssueType && issueTypeOptions.length > 0) {
      setFormData(prev => ({ ...prev, issueType: issueTypeOptions[0].value }));
    }
  }, [initialIssueType, issueTypeOptions]);

  function getPageName(pathname) {
    const pageNames = {
      '/': 'Home',
      '/game-library-dashboard': 'Game Library',
      '/game-detail-launch': 'Game Detail',
      '/subscription-management': 'Subscription Management',
      '/user-profile-settings': 'User Profile',
      '/privacy-policy': 'Privacy Policy',
      '/terms-of-use': 'Terms of Use',
      '/community-guidelines': 'Community Guidelines',
      '/admin-dashboard': 'Admin Dashboard',
      '/favorites': 'Favorites',
      '/library': 'My Library',
      '/notifications': 'Notifications'
    };

    // Check for dynamic routes
    if (pathname.startsWith('/games/')) return 'Game Detail';
    if (pathname.startsWith('/admin-')) return 'Admin Dashboard';
    
    return pageNames[pathname] || 'Unknown Page';
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.issueType || !formData.subject || !formData.message) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Collect additional metadata
      const contactMetadata = {
        ...metadata,
        browser: navigator.userAgent,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        referrer: document.referrer,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        userAgent: navigator.userAgent,
        language: navigator.language
      };

      const result = await contactService.createContact({
        issueType: formData.issueType,
        subject: formData.subject,
        message: formData.message,
        sourcePage: detectedSourcePage,
        sourceUrl: detectedSourceUrl,
        metadata: contactMetadata,
        emailNotifications: user ? formData.emailNotifications : false
      });

      if (result.success) {
        setSuccess(true);
        setFormData({ issueType: '', subject: '', message: '' });
        
        if (onSuccess) {
          onSuccess(result.data);
        }
      } else {
        setError(result.error || 'Failed to submit contact form');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Contact form error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (success && !compact) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={32} className="text-green-500" />
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-2">
            Message Sent Successfully!
          </h3>
          <p className="text-muted-foreground mb-6">
            Thank you for contacting us. We've received your message and will respond as soon as possible.
            {user && " You can track the status of your inquiry in your notifications."}
          </p>
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Close
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-lg ${compact ? 'p-4' : 'p-6'}`}>
      {showTitle && (
        <div className="mb-6">
          <h2 className={`font-semibold text-foreground mb-2 ${compact ? 'text-lg' : 'text-2xl'}`}>
            Contact Support
          </h2>
          <p className="text-muted-foreground text-sm">
            {detectedSourcePage !== 'Unknown Page' && `Reporting from: ${detectedSourcePage}`}
            {!user && " • You can submit anonymously or sign in to track your inquiry"}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Issue Type */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Issue Type *
          </label>
          <Select
            value={formData.issueType}
            onChange={(value) => handleInputChange('issueType', value)}
            options={issueTypeOptions}
            placeholder="Select an issue type"
            required
          />
        </div>

        {/* Subject */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Subject *
          </label>
          <Input
            type="text"
            value={formData.subject}
            onChange={(e) => handleInputChange('subject', e.target.value)}
            placeholder="Brief description of your issue"
            required
            maxLength={200}
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Message *
          </label>
          <textarea
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            placeholder="Please provide detailed information about your issue..."
            required
            rows={compact ? 4 : 6}
            maxLength={2000}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
          />
          <div className="text-xs text-muted-foreground mt-1">
            {formData.message.length}/2000 characters
          </div>
        </div>

        {/* Email Notifications Checkbox - Only show for logged-in users */}
        {user && (
          <div className="flex items-start space-x-3 p-3 bg-muted/30 rounded-lg border border-border">
            <input
              type="checkbox"
              id="emailNotifications"
              checked={formData.emailNotifications}
              onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
            />
            <div className="flex-1">
              <label htmlFor="emailNotifications" className="text-sm font-medium text-foreground cursor-pointer">
                Email me when there's a response
              </label>
              <p className="text-xs text-muted-foreground mt-1">
                You'll receive an in-app notification and an email when our support team responds to your ticket.
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <span className="text-sm text-destructive">{error}</span>
            </div>
          </div>
        )}

        {/* Success Message (compact mode) */}
        {success && compact && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-green-500" />
              <span className="text-sm text-green-500">Message sent successfully!</span>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className={`flex ${compact ? 'flex-col space-y-2' : 'flex-row space-x-3'} pt-2`}>
          <Button
            type="submit"
            loading={loading}
            disabled={!formData.issueType || !formData.subject || !formData.message}
            className={compact ? 'w-full' : 'flex-1'}
          >
            {loading ? 'Sending...' : 'Send Message'}
          </Button>
          
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className={compact ? 'w-full' : 'flex-1'}
            >
              Cancel
            </Button>
          )}
        </div>

        {/* Additional Info */}
        <div className="text-xs text-muted-foreground pt-2 border-t border-border">
          <p>
            By submitting this form, you agree to our{' '}
            <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>.
            {!user && ' Consider signing in to track your inquiry status.'}
          </p>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
